import type {
  ComponentType,
  InputHTMLAttributes,
  ReactNode,
  SVGProps,
  TextareaHTMLAttributes,
} from "react";
import { forwardRef, useId } from "react";
import { cn } from "../../lib/utils";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

const FIELD_BOX =
  "flex w-full items-center rounded-2xl border border-black/10 bg-white shadow-card transition-colors duration-150 hover:border-primary/40 focus-within:border-primary/40";
const FIELD_INVALID = "border-error hover:border-error focus-within:border-error";
const FIELD_INPUT =
  "w-full min-w-0 bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none disabled:cursor-not-allowed";

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: IconType;
  /** Anything rendered inside the box on the right: a show-password button, a unit. */
  trailingSlot?: ReactNode;
  invalid?: boolean;
  /** Class for the outer box; `className` goes to the <input>. */
  boxClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    leadingIcon: Leading,
    trailingSlot,
    invalid = false,
    boxClassName,
    className,
    disabled,
    ...props
  },
  ref
) {
  return (
    <div
      className={cn(
        FIELD_BOX,
        "h-11 px-4",
        invalid && FIELD_INVALID,
        disabled && "bg-surface-2 opacity-70",
        boxClassName
      )}
    >
      {Leading && (
        <Leading
          aria-hidden="true"
          className="mr-2.5 size-4 shrink-0 text-ink-faint"
        />
      )}
      <input
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(FIELD_INPUT, "h-full", className)}
        {...props}
      />
      {trailingSlot && (
        <div className="ml-2 flex shrink-0 items-center text-ink-faint">
          {trailingSlot}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Textarea
// ---------------------------------------------------------------------------

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  boxClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { invalid = false, boxClassName, className, disabled, rows = 4, ...props },
    ref
  ) {
    return (
      <div
        className={cn(
          FIELD_BOX,
          "px-4 py-3",
          invalid && FIELD_INVALID,
          disabled && "bg-surface-2 opacity-70",
          boxClassName
        )}
      >
        <textarea
          ref={ref}
          rows={rows}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(FIELD_INPUT, "resize-y leading-relaxed", className)}
          {...props}
        />
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// Field — label / control / hint / error
// ---------------------------------------------------------------------------

export interface FieldProps {
  label: ReactNode;
  /** Pass the control's id so the label targets it; one is generated otherwise. */
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Right-aligned slot beside the label, e.g. "Forgot password?" */
  labelAction?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode | ((id: string) => ReactNode);
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  labelAction,
  required,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const id = htmlFor ?? generated;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-xs font-semibold text-ink">
          {label}
          {required && (
            <span aria-hidden="true" className="ml-0.5 text-primary">
              *
            </span>
          )}
        </label>
        {labelAction && (
          <span className="text-xs font-medium text-primary">{labelAction}</span>
        )}
      </div>
      {typeof children === "function" ? children(id) : children}
      {error ? (
        <p role="alert" className="text-xs font-medium text-error">
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-ink-muted">{hint}</p>
      )}
    </div>
  );
}
